















































import { Vue, Component, Prop, Watch } from "@/shared/vuepack";
import { FamilyMember, FamilyMemberRelationship, FamilyMemberStatus } from "@/shared/types";
import { Validator } from "vee-validate";
import * as _ from "lodash";
import { swiper, swiperSlide } from "vue-awesome-swiper";
import FamilyMemberComponent from "@/employee/components/onboarding/FamilyMemberComponent.vue";
import AddFamilyMember from "@/employee/components/onboarding/AddFamilyMember.vue";
import Button from "@/shared/components/ui/Button.vue";
import { useNotifierStore } from "@/shared/store/notifier";
import { useFamilyStore } from "@/employee/store/family";
import { useCustodianSettingsStore } from "@/shared/store/custodianSettings";
import { useWizardStore } from "@/employee/store/wizard";

const maximumNumberOfDependentCards = 3;
const intialFamilyMember: VueFamily = {
  id: null,
  firstName: "",
  middleInitial: "",
  lastName: "",
  fullName: "",
  tag: "",
  dateOfBirth: null,
  debitCardHolder: false,
  ssn: "",
  index: 0,
  relationship: FamilyMemberRelationship.None,
  isStudent: null,
  status: FamilyMemberStatus.None
};

interface VueFamily extends FamilyMember {
  index: number;
}

@Component({
  components: {
    FamilyMemberComponent,
    AddFamilyMember,
    Button,
    swiper,
    swiperSlide
  }
})
export default class Family extends Vue {
  familyStore = useFamilyStore();
  notifierStore = useNotifierStore();
  custodianSettingsStore = useCustodianSettingsStore();
  wizardStore = useWizardStore();

  $validator: Validator;
  isSubmitting = false;
  isLoaded = false;

  isShowFamilyMemberVisible = false;
  owner: FamilyMember = intialFamilyMember;
  formModel: VueFamily[] = [];
  itemIndex = 0;
  showAsSlider = false;
  familyMemberWasRemoved = false;

  @Prop({ required: true })
  onBack: (hasUnsavedChanges: boolean) => void;

  addFamilyMember (tag) {
    const newFamilyMember = { ...intialFamilyMember };
    newFamilyMember.tag = "";
    newFamilyMember.index = this.itemIndex++;
    this.formModel.push(newFamilyMember);
  }

  removeFamilyMember (index) {
    this.formModel.splice(index, 1);
    this.familyMemberWasRemoved = true;
  }

  showAddNewFamilyMember () {
    this.isShowFamilyMemberVisible = true;
  }

  get hasUnsavedChanges () {
    return this.$validator.fields.items.some((f: any) => f.flags.changed) || this.familyMemberWasRemoved;
  }

  get shouldShowAddFamilyMember () {
    return this.isShowFamilyMemberVisible;
  }

  get displayAddDependentButton (): boolean {
    return this.formModel.length < 10;
  }

  get shouldShowNextButton () {
    return this.formModel.length;
  }

  get shouldShowSkipButton () {
    return !this.wizardStore.isStepComplete("your-family");
  }

  get numberOfCards () {
    return this.formModel &&
      this.formModel.reduce((accumulator: number, model) =>
        accumulator + (model.debitCardHolder ? 1 : 0), 0);
  }

  @Watch("familyStore.family", { deep: true })
  onPlanStateChanged () {
    this.initializeFromStateValue();
  }

  @Watch("numberOfCards")
  updateNumberOfCardsError (numberOfCardsValue) {
    const errorBag = this.$validator.errors;
    const hasError = errorBag.has(this.cardCountErrorField);
    if (numberOfCardsValue > maximumNumberOfDependentCards && !hasError) {
      errorBag.add({
        field: this.cardCountErrorField,
        msg: `Cannot send more than ${maximumNumberOfDependentCards} cards to dependents`
      });
    } else if (numberOfCardsValue <= maximumNumberOfDependentCards && hasError) {
      errorBag.remove(this.cardCountErrorField);
    }
  }

  @Watch("hasUnsavedChanges")
  onHasUnsavedChangesChanged () {
    if (this.hasUnsavedChanges) {
      window.addEventListener("beforeunload", this.beforeUnloadHandler);
    } else {
      window.removeEventListener("beforeunload", this.beforeUnloadHandler);
    }
  }

  beforeUnloadHandler (event: BeforeUnloadEvent) {
    event.preventDefault();
    event.returnValue = true;
  }

  cloneToCurrentModel (o) {
    const clonedModel = _.cloneDeep(o);
    for (const item of clonedModel) {
      item.index = this.itemIndex++;
    }
    this.formModel = clonedModel;
    this.isLoaded = true;
  }

  initializeFromStateValue () {
    const familyMembers = this.familyStore.familyMembers;
    if (!familyMembers) {
      return;
    } else if (familyMembers) {
      this.cloneToCurrentModel(familyMembers);
    } else {
      this.isLoaded = false;
    }

    this.familyMemberWasRemoved = false;
  }

  mounted (): void {
    this.initializeFromStateValue();
    this.familyStore.initialize().catch(this.handleError);
    window.addEventListener("resize", this.handleResize);
    this.handleResize();
  }

  handleResize () {
    // this.updateSliderWidth(window.innerWidth);
  }

  beforeDestroy () {
    window.removeEventListener("beforeunload", this.beforeUnloadHandler);
    window.removeEventListener("resize", this.handleResize);
  }

  onSubmit (): void {
    if (this.isSubmitting) {
      return;
    }

    this.validateAllScopes()
      .then(result => {
        if (!result) {
          return;
        }
        this.isSubmitting = true;
        this.familyStore.update({ newFamilyMembers: this.formModel })
          .then(() => {
            this.isSubmitting = false;
          })
          .then(() => this.$emit("complete"))
          .catch(err => {
            this.isSubmitting = false;
            this.handleError(err);
          });
      });
  }

  validateAllScopes (): Promise<boolean> {
    const scopeValidations = this.formModel.map(member => this.$validator.validateAll(`${member.index}`));

    return this.$validator.validateAll().then(v => {
      if (!v) {
        return v;
      }
      return Promise.all(scopeValidations)
        .then(values => !values.some(validationResult => !validationResult));
    });
  }

  get cardCountErrorField () {
    return "dependent debit card count";
  }

  showError (message: string): void {
    this.notifierStore.setDanger(message);
    window.scrollTo({
      behavior: "smooth",
      left: 0,
      top: 0
    });
  }

  handleError (err: any): void {
    this.isSubmitting = false;
    let message = "Error processing your request. Please try again later";

    if (!err) {
      this.showError(message);
      return;
    }

    if (err.payload) {
      err = err.payload;
    }

    if (err.response) {
      err = err.response;
    }

    if (err.data) {
      err = err.data;
    }

    if (err.errorMessage) {
      message = err.errorMessage;
    } else if (err.message) {
      message = err.message;
    }

    this.showError((err && err.errorMessage) || message);
  }

  completeFamilyStep () {
    this.$emit("complete");
  }

  get isHsaBank (): boolean {
    return this.custodianSettingsStore.isHsaBankCustodian;
  }

  get hasDuplicateSsns (): boolean {
    if (!this.isHsaBank) {
      return false;
    }

    const familyMembers = this.formModel;
    const groupedBySsn = _.groupBy(familyMembers, "ssn");
    const familyMembersWithDuplicateSsn = _.filter(groupedBySsn, group => group.length > 1);

    return !!familyMembersWithDuplicateSsn.length;
  }

  get submitDisabled (): boolean {
    return this.$validator.errors.any() || this.hasDuplicateSsns || this.isSubmitting;
  }

  goBack () {
    this.onBack(this.hasUnsavedChanges);
  }
}
